/**
 * Note boards.
 *
 * You can read every board from anywhere — that is the point of a shared
 * office — but pinning or editing requires standing next to the board, which
 * the server enforces independently. The panel just reflects that rule.
 */

import { NOTE_COLORS, zoneById, type Note, type Surface } from '@virtual-office/shared';

import type { Store } from '../state.js';
import { $, clear, el, relativeTime } from './dom.js';

export interface BoardCallbacks {
  create(surfaceId: string, title: string, body: string, color: string): void;
  update(id: string, title: string, body: string, color: string): void;
  remove(id: string): void;
}

export class BoardsPanel {
  private readonly panel = $('boards');
  private readonly title = $('boards-title');
  private readonly tabs = $('boards-tabs');
  private readonly hint = $('boards-hint');
  private readonly list = $('notes-list');
  private readonly form = $<HTMLFormElement>('note-form');
  private readonly titleInput = $<HTMLInputElement>('note-title');
  private readonly bodyInput = $<HTMLTextAreaElement>('note-body');
  private readonly newButton = $<HTMLButtonElement>('note-new');
  private readonly saveButton = $<HTMLButtonElement>('note-save');

  private selectedSurfaceId: string | null = null;
  private nearbySurface: Surface | null = null;
  private editingNoteId: string | null = null;
  private noteColor: string = NOTE_COLORS[0];

  constructor(
    private readonly store: Store,
    private readonly callbacks: BoardCallbacks,
  ) {
    $('boards-close').addEventListener('click', () => this.close());
    this.newButton.addEventListener('click', () => this.startCreate());
    $('note-cancel').addEventListener('click', () => this.closeForm());

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    const fields: HTMLElement[] = [this.titleInput, this.bodyInput];
    for (const field of fields) {
      field.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeForm();
          event.stopPropagation();
        }
        // Enter in the body should make a new line, not submit.
        if (event.key === 'Enter' && event.target === this.titleInput) {
          event.preventDefault();
          this.bodyInput.focus();
        }
      });
    }

    this.store.on('notes', () => this.render());
    this.store.on('welcome', () => this.render());
    this.renderColorSwatches();
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  get activeSurfaceId(): string | null {
    return this.isOpen ? this.selectedSurfaceId : null;
  }

  /** Called every frame from the game loop as the player walks around. */
  setNearbySurface(surface: Surface | null): void {
    const changed = this.nearbySurface?.id !== surface?.id;
    this.nearbySurface = surface;
    if (changed && this.isOpen) this.render();
  }

  private boards(): Surface[] {
    return this.store.map?.surfaces.filter((s) => s.kind === 'noteboard') ?? [];
  }

  open(surfaceId?: string): void {
    const boards = this.boards();
    if (boards.length === 0) return;
    this.selectedSurfaceId =
      surfaceId ?? this.selectedSurfaceId ?? this.nearbySurface?.id ?? boards[0]?.id ?? null;
    this.panel.hidden = false;
    this.closeForm();
    this.render();
  }

  close(): void {
    this.panel.hidden = true;
    this.closeForm();
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  /** True when the selected board is close enough to write to. */
  private canWrite(): boolean {
    return this.nearbySurface !== null && this.nearbySurface.id === this.selectedSurfaceId;
  }

  private renderColorSwatches(): void {
    const host = $('note-colors');
    clear(host);
    for (const color of NOTE_COLORS) {
      const button = el('button', { className: 'swatch', title: color });
      button.type = 'button';
      button.style.background = color;
      button.setAttribute('aria-label', `Note colour ${color}`);
      if (color === this.noteColor) button.classList.add('selected');
      button.addEventListener('click', () => {
        this.noteColor = color;
        this.renderColorSwatches();
      });
      host.append(button);
    }
  }

  private startCreate(): void {
    if (!this.canWrite()) return;
    this.editingNoteId = null;
    this.titleInput.value = '';
    this.bodyInput.value = '';
    this.saveButton.textContent = 'Pin note';
    this.form.hidden = false;
    this.newButton.hidden = true;
    this.titleInput.focus();
  }

  private startEdit(note: Note): void {
    if (!this.canWrite()) return;
    this.editingNoteId = note.id;
    this.titleInput.value = note.title;
    this.bodyInput.value = note.body;
    this.noteColor = note.color;
    this.renderColorSwatches();
    this.saveButton.textContent = 'Save changes';
    this.form.hidden = false;
    this.newButton.hidden = true;
    this.titleInput.focus();
  }

  private closeForm(): void {
    this.form.hidden = true;
    this.editingNoteId = null;
    this.newButton.hidden = false;
  }

  private submit(): void {
    const surfaceId = this.selectedSurfaceId;
    if (!surfaceId) return;
    const title = this.titleInput.value.trim();
    const body = this.bodyInput.value.trim();
    if (!title && !body) return;

    if (this.editingNoteId) {
      this.callbacks.update(this.editingNoteId, title || 'Untitled', body, this.noteColor);
    } else {
      this.callbacks.create(surfaceId, title || 'Untitled', body, this.noteColor);
    }
    this.closeForm();
  }

  private render(): void {
    const map = this.store.map;
    if (!map) return;
    const boards = this.boards();
    if (!this.selectedSurfaceId) this.selectedSurfaceId = boards[0]?.id ?? null;

    clear(this.tabs);
    for (const board of boards) {
      const zone = zoneById(map, board.zoneId);
      const count = this.store.notesFor(board.id).length;
      const tab = el('button', {
        className: 'board-tab',
        text: count > 0 ? `${board.label} (${count})` : board.label,
        title: zone?.name ?? '',
      });
      tab.type = 'button';
      if (board.id === this.selectedSurfaceId) tab.classList.add('selected');
      if (board.id === this.nearbySurface?.id) tab.classList.add('reachable');
      tab.addEventListener('click', () => {
        this.selectedSurfaceId = board.id;
        this.closeForm();
        this.render();
      });
      this.tabs.append(tab);
    }

    const selected = boards.find((b) => b.id === this.selectedSurfaceId) ?? null;
    const zone = selected ? zoneById(map, selected.zoneId) : null;
    this.title.textContent = selected ? selected.label : 'Boards';

    const writable = this.canWrite();
    this.newButton.disabled = !writable;
    this.hint.textContent = writable
      ? 'You are at this board — pin and edit away.'
      : selected
        ? `Read-only from here. Walk to ${zone?.name ?? 'the board'} to edit.`
        : '';

    clear(this.list);
    const notes = selected ? this.store.notesFor(selected.id) : [];
    if (notes.length === 0) {
      this.list.append(el('p', { className: 'empty', text: 'Nothing pinned here yet.' }));
    }
    for (const note of notes) {
      this.list.append(this.renderNote(note, writable));
    }
  }

  private renderNote(note: Note, writable: boolean): HTMLElement {
    const card = el('article', { className: 'note' });
    card.style.background = note.color;

    card.append(
      el('h3', { className: 'note-title', text: note.title }),
      el('p', { className: 'note-body', text: note.body }),
      el('footer', {
        className: 'note-meta',
        text: `${note.authorName} · ${relativeTime(note.updatedAt)}`,
      }),
    );

    if (writable) {
      const edit = el('button', { className: 'ghost small', text: 'Edit' });
      edit.type = 'button';
      edit.addEventListener('click', () => this.startEdit(note));

      const remove = el('button', { className: 'ghost small danger', text: 'Delete' });
      remove.type = 'button';
      remove.addEventListener('click', () => {
        if (confirm(`Delete “${note.title}”?`)) this.callbacks.remove(note.id);
      });

      card.append(el('div', { className: 'note-actions' }, [edit, remove]));
    }
    return card;
  }
}
